import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';

// Funkce pro formátování čísel s mezerami jako oddělovači tisíců
const formatNumber = (num) => {
  return new Intl.NumberFormat('cs-CZ', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(num);
};

export default function ComplaintsTab({ userRole, currentUserId, isDarkMode, prefillCompany }) {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('open');
  const [showNewComplaintModal, setShowNewComplaintModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [showAddCommissionModal, setShowAddCommissionModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);

  // Formulář pro novou reklamaci
  const [newComplaint, setNewComplaint] = useState({
    companyName: prefillCompany?.companyName || '',
    ico: prefillCompany?.ico || '',
    description: ''
  });

  // Otevři modal pokud přichází prefill z Leady tabu
  useEffect(() => {
    if (prefillCompany?.companyName) {
      setNewComplaint({ companyName: prefillCompany.companyName, ico: prefillCompany.ico || '', description: '' });
      setShowNewComplaintModal(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Formulář pro vyřešení reklamace
  const [resolveData, setResolveData] = useState({
    adminNote: ''
  });

  // Formulář pro přidání provize
  const [commissionData, setCommissionData] = useState({
    companyName: '',
    ico: '',
    commission: '',
    month: getCurrentMonth()
  });

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const isAdmin = userRole === 'admin';
      let url = `${API_URL}/complaints?userId=${currentUserId}&isAdmin=${isAdmin}`;
      
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setComplaints(data.complaints || []);
      }
    } catch (error) {
      console.error('Chyba při načítání reklamací:', error);
    } finally {
      setLoading(false);
    }
  };

  const createComplaint = async () => {
    console.log('🔵 Starting createComplaint...');
    console.log('Current complaint data:', newComplaint);
    console.log('Current user ID:', currentUserId);
    
    if (!newComplaint.companyName || !newComplaint.description) {
      alert('Vyplňte prosím název firmy a popis reklamace');
      return;
    }

    try {
      const requestBody = {
        userId: currentUserId,
        companyName: newComplaint.companyName.trim(),
        ico: newComplaint.ico ? newComplaint.ico.trim() : '',
        description: newComplaint.description.trim()
      };
      console.log('📤 Sending request to:', `${API_URL}/complaints`);
      console.log('📦 Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`${API_URL}/complaints`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('📨 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Complaint created successfully:', data);
        alert('Reklamace byla úspěšně vytvořena');
        setShowNewComplaintModal(false);
        setNewComplaint({ companyName: '', ico: '', description: '' });
        fetchComplaints();
      } else {
        const errorData = await response.json();
        console.error('❌ Error response:', errorData);
        
        let errorMessage = 'Chyba při vytváření reklamace:\n';
        if (errorData.error) errorMessage += errorData.error + '\n';
        if (errorData.details) errorMessage += 'Detaily: ' + errorData.details + '\n';
        if (errorData.missingFields) errorMessage += 'Chybí pole: ' + errorData.missingFields.join(', ');
        if (errorData.validationErrors) {
          errorMessage += 'Validační chyby:\n';
          errorData.validationErrors.forEach(err => {
            errorMessage += `  - ${err.field}: ${err.message}\n`;
          });
        }
        
        alert(errorMessage);
      }
    } catch (error) {
      console.error('❌ Exception occurred:', error);
      console.error('Error stack:', error.stack);
      alert('Chyba při vytváření reklamace: ' + error.message);
    }
  };

  const updateNote = async () => {
    if (!selectedComplaint) return;

    try {
      const response = await fetch(`${API_URL}/complaints/${selectedComplaint._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          adminNote: resolveData.adminNote
        })
      });

      if (response.ok) {
        alert('Poznámka byla aktualizována');
        setShowEditNoteModal(false);
        setResolveData({ adminNote: '' });
        setSelectedComplaint(null);
        fetchComplaints();
      } else {
        alert('Chyba při aktualizaci poznámky');
      }
    } catch (error) {
      console.error('Chyba při aktualizaci poznámky:', error);
      alert('Chyba při aktualizaci poznámky');
    }
  };

  const resolveComplaint = async () => {
    if (!selectedComplaint) return;

    try {
      const response = await fetch(`${API_URL}/complaints/${selectedComplaint._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'resolved',
          adminNote: resolveData.adminNote,
          resolvedBy: currentUserId
        })
      });

      if (response.ok) {
        alert('Reklamace byla označena jako vyřešená');
        setShowResolveModal(false);
        setResolveData({ adminNote: '' });
        setSelectedComplaint(null);
        fetchComplaints();
      } else {
        alert('Chyba při vyřešení reklamace');
      }
    } catch (error) {
      console.error('Chyba při vyřešení reklamace:', error);
      alert('Chyba při vyřešení reklamace');
    }
  };

  const addCommissionFromComplaint = async () => {
    if (!selectedComplaint) return;
    
    if (!commissionData.companyName || !commissionData.commission) {
      alert('Vyplňte prosím název firmy a provizi');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/complaints/${selectedComplaint._id}/add-commission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: commissionData.companyName,
          ico: commissionData.ico,
          commission: parseFloat(commissionData.commission),
          userId: selectedComplaint.userId._id || selectedComplaint.userId,
          month: commissionData.month
        })
      });

      if (response.ok) {
        alert('Provize byla úspěšně přidána do hlavních provizí');
        setShowAddCommissionModal(false);
        setCommissionData({ companyName: '', ico: '', commission: '', month: getCurrentMonth() });
        setSelectedComplaint(null);
      } else {
        const errorData = await response.json();
        alert('Chyba při přidávání provize: ' + (errorData.error || 'Neznámá chyba'));
      }
    } catch (error) {
      console.error('Chyba při přidávání provize:', error);
      alert('Chyba při přidávání provize');
    }
  };

  const deleteComplaint = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tuto reklamaci?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/complaints/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Reklamace byla smazána');
        fetchComplaints();
      } else {
        alert('Chyba při mazání reklamace');
      }
    } catch (error) {
      console.error('Chyba při mazání reklamace:', error);
      alert('Chyba při mazání reklamace');
    }
  };

  useEffect(() => {
    fetchComplaints();
  }, [statusFilter]);

  return (
    <div>
      {/* Horní panel */}
      <div className={`mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mr-2`}>Filtr:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`border rounded px-3 py-1 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="">Vše</option>
                <option value="open">Otevřené</option>
                <option value="resolved">Vyřešené</option>
              </select>
            </div>
            <button
              onClick={fetchComplaints}
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm disabled:opacity-50"
            >
              {loading ? 'Načítání...' : 'Obnovit'}
            </button>
          </div>
          <button
            onClick={() => setShowNewComplaintModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium"
          >
            ➕ Nová reklamace
          </button>
        </div>
      </div>

      {/* Souhrn */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-orange-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Otevřené reklamace</h3>
          <p className="text-2xl font-bold text-orange-600">
            {complaints.filter(c => c.status === 'open').length}
          </p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-green-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Vyřešené reklamace</h3>
          <p className="text-2xl font-bold text-green-600">
            {complaints.filter(c => c.status === 'resolved').length}
          </p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-blue-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Celkem reklamací</h3>
          <p className="text-2xl font-bold text-blue-600">{formatNumber(complaints.length)}</p>
        </div>
      </div>

      {/* Tabulka reklamací */}
      {loading ? (
        <div className="text-center py-8">Načítání reklamací...</div>
      ) : (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
          {complaints.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Žádné reklamace
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Datum</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Název firmy</th>
                    <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>IČO</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Popis</th>
                    {userRole === 'admin' && (
                      <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Uživatel</th>
                    )}
                    <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Status</th>
                    <th className={`px-4 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Poznámka admina</th>
                    <th className={`px-4 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-500'} uppercase`}>Akce</th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                  {complaints.map((complaint) => (
                    <tr key={complaint._id} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                      <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {new Date(complaint.createdAt).toLocaleDateString('cs-CZ')}
                      </td>
                      <td className={`px-4 py-3 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {complaint.companyName}
                      </td>
                      <td className={`px-4 py-3 text-sm text-center font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {complaint.ico || '-'}
                      </td>
                      <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} max-w-xs`}>
                        <div className="truncate" title={complaint.description}>
                          {complaint.description}
                        </div>
                      </td>
                      {userRole === 'admin' && (
                        <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                          {complaint.userId?.name || complaint.userName || '-'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-center">
                        {complaint.status === 'open' ? (
                          <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">
                            Otevřená
                          </span>
                        ) : (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                            Vyřešená
                          </span>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'} max-w-xs`}>
                        <div className="truncate" title={complaint.adminNote}>
                          {complaint.adminNote || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedComplaint(complaint);
                              setResolveData({ adminNote: complaint.adminNote || '' });
                              setShowDetailModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="Zobrazit detail"
                          >
                            👁️
                          </button>
                          {userRole === 'admin' && (
                            <button
                              onClick={() => {
                                setSelectedComplaint(complaint);
                                setResolveData({ adminNote: complaint.adminNote || '' });
                                setShowEditNoteModal(true);
                              }}
                              className="text-orange-600 hover:text-orange-800"
                              title="Upravit poznámku"
                            >
                              ✏️
                            </button>
                          )}
                          {userRole === 'admin' && complaint.status === 'open' && (
                            <button
                              onClick={() => {
                                setSelectedComplaint(complaint);
                                setResolveData({ adminNote: complaint.adminNote || '' });
                                setShowResolveModal(true);
                              }}
                              className="text-green-600 hover:text-green-800"
                              title="Označit jako vyřešené"
                            >
                              ✓
                            </button>
                          )}
                          {userRole === 'admin' && complaint.status === 'resolved' && (
                            <button
                              onClick={() => {
                                setSelectedComplaint(complaint);
                                setCommissionData({
                                  companyName: complaint.companyName,
                                  ico: complaint.ico || '',
                                  commission: '',
                                  month: getCurrentMonth()
                                });
                                setShowAddCommissionModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800"
                              title="Přidat provizi"
                            >
                              💰
                            </button>
                          )}
                          {userRole === 'admin' && (
                            <button
                              onClick={() => deleteComplaint(complaint._id)}
                              className="text-red-600 hover:text-red-800"
                              title="Smazat"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal - Nová reklamace */}
      {showNewComplaintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Nová reklamace</h2>
              <button
                onClick={() => setShowNewComplaintModal(false)}
                className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Název firmy *
                  </label>
                  <input
                    type="text"
                    value={newComplaint.companyName}
                    onChange={(e) => setNewComplaint({...newComplaint, companyName: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    IČO
                  </label>
                  <input
                    type="text"
                    value={newComplaint.ico}
                    onChange={(e) => setNewComplaint({...newComplaint, ico: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Popis reklamace *
                  </label>
                  <textarea
                    value={newComplaint.description}
                    onChange={(e) => setNewComplaint({...newComplaint, description: e.target.value})}
                    rows="4"
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowNewComplaintModal(false)}
                  className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Zrušit
                </button>
                <button
                  onClick={createComplaint}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  Vytvořit reklamaci
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Upravit poznámku */}
      {showEditNoteModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Upravit poznámku</h2>
              <button
                onClick={() => setShowEditNoteModal(false)}
                className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <div className={`mb-4 p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Firma:</strong> {selectedComplaint.companyName}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Popis:</strong> {selectedComplaint.description}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Status:</strong> {selectedComplaint.status === 'open' ? 'Otevřená' : 'Vyřešená'}
                </p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Poznámka admina
                </label>
                <textarea
                  value={resolveData.adminNote}
                  onChange={(e) => setResolveData({...resolveData, adminNote: e.target.value})}
                  rows="3"
                  className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Poznámka admina k reklamaci..."
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowEditNoteModal(false)}
                  className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Zrušit
                </button>
                <button
                  onClick={updateNote}
                  className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700"
                >
                  Uložit poznámku
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Vyřešit reklamaci */}
      {showResolveModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Vyřešit reklamaci</h2>
              <button
                onClick={() => setShowResolveModal(false)}
                className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <div className={`mb-4 p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Firma:</strong> {selectedComplaint.companyName}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <strong>Popis:</strong> {selectedComplaint.description}
                </p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Poznámka admina
                </label>
                <textarea
                  value={resolveData.adminNote}
                  onChange={(e) => setResolveData({...resolveData, adminNote: e.target.value})}
                  rows="3"
                  className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Jak byla reklamace vyřešena..."
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowResolveModal(false)}
                  className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Zrušit
                </button>
                <button
                  onClick={resolveComplaint}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  Označit jako vyřešené
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Přidat provizi */}
      {showAddCommissionModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg max-w-2xl w-full m-4`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Přidat provizi z reklamace</h2>
              <button
                onClick={() => setShowAddCommissionModal(false)}
                className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Název firmy *
                  </label>
                  <input
                    type="text"
                    value={commissionData.companyName}
                    onChange={(e) => setCommissionData({...commissionData, companyName: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    IČO
                  </label>
                  <input
                    type="text"
                    value={commissionData.ico}
                    onChange={(e) => setCommissionData({...commissionData, ico: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Provize (Kč) *
                  </label>
                  <input
                    type="number"
                    value={commissionData.commission}
                    onChange={(e) => setCommissionData({...commissionData, commission: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Měsíc *
                  </label>
                  <input
                    type="month"
                    value={commissionData.month}
                    onChange={(e) => setCommissionData({...commissionData, month: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <strong>Uživatel:</strong> {selectedComplaint.userId?.name || selectedComplaint.userName || '-'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                    Provize bude přidána do hlavních provizí tohoto uživatele
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setShowAddCommissionModal(false)}
                  className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Zrušit
                </button>
                <button
                  onClick={addCommissionFromComplaint}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Přidat provizi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pro zobrazení detailu reklamace */}
      {showDetailModal && selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}>
            <div className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} px-6 py-4 flex justify-between items-center`}>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Detail reklamace
              </h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className={`${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                  Datum vytvoření
                </label>
                <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {new Date(selectedComplaint.createdAt).toLocaleString('cs-CZ')}
                </p>
              </div>
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                  Název firmy
                </label>
                <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-medium`}>
                  {selectedComplaint.companyName}
                </p>
              </div>
              {selectedComplaint.ico && (
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    IČO
                  </label>
                  <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} font-mono`}>
                    {selectedComplaint.ico}
                  </p>
                </div>
              )}
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                  Popis reklamace
                </label>
                <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} whitespace-pre-wrap ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} p-3 rounded`}>
                  {selectedComplaint.description}
                </p>
              </div>
              {userRole === 'admin' && (
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Uživatel
                  </label>
                  <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedComplaint.userId?.name || selectedComplaint.userName || '-'}
                  </p>
                </div>
              )}
              <div>
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                  Status
                </label>
                <p>
                  {selectedComplaint.status === 'open' ? (
                    <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded text-sm">
                      Otevřená
                    </span>
                  ) : (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded text-sm">
                      Vyřešená
                    </span>
                  )}
                </p>
              </div>
              {userRole === 'admin' && (
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Poznámka admina
                  </label>
                  <textarea
                    value={resolveData.adminNote}
                    onChange={(e) => setResolveData({...resolveData, adminNote: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    rows="4"
                    placeholder="Napište poznámku k reklamaci..."
                  />
                </div>
              )}
              {!userRole === 'admin' && selectedComplaint.adminNote && (
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Poznámka admina
                  </label>
                  <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'} whitespace-pre-wrap ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} p-3 rounded`}>
                    {selectedComplaint.adminNote}
                  </p>
                </div>
              )}
              {selectedComplaint.resolvedAt && (
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`}>
                    Datum vyřešení
                  </label>
                  <p className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {new Date(selectedComplaint.resolvedAt).toLocaleString('cs-CZ')}
                  </p>
                </div>
              )}
            </div>
            <div className={`sticky bottom-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} px-6 py-4 flex justify-end gap-2`}>
              <button
                onClick={() => setShowDetailModal(false)}
                className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Zavřít
              </button>
              {userRole === 'admin' && (
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/complaints/${selectedComplaint._id}/note`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ adminNote: resolveData.adminNote })
                      });
                      if (response.ok) {
                        alert('Poznámka byla uložena');
                        setShowDetailModal(false);
                        fetchComplaints();
                      } else {
                        alert('Chyba při ukládání poznámky');
                      }
                    } catch (error) {
                      console.error('Error saving note:', error);
                      alert('Chyba při ukládání poznámky');
                    }
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Uložit poznámku
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
